// @LTG-ID: lib-url-utils
// @STATUS: BUILT @SHARED @EXTERNAL @PROPOSED-CONTRACT
// @HERMETIC: true
// @DOCS: 10_DECISIONS.md#ADR-005, 10_DECISIONS.md#ADR-008, 10_DECISIONS.md#ADR-009 (ACCEPTED CHG-0031), 04b_SHARED_CONTRACT.md
// @CHANGED: 2026-02 session_002 (CHG-0037 — drift-repair ADR-009 status PROPOSED→ACCEPTED)
// @OWNER: web
//
// Single source of truth for outbound app.letstalkgrowth.com URL decoration + the
// first-paint attribution snapshot.
//
// === Merge rules (ADR-008) ===
//   1. Existing query params on the destination href are preserved unless explicitly
//      overridden by componentParams.
//   2. Inbound UTM + click-id params are merged onto the destination from
//      a) sessionStorage snapshot first (per-key first-hop-wins)
//      b) window.location.search as fallback for keys not in the snapshot
//   3. Inbound `sector=<key>` is copied unless componentParams.sector is set.
//   4. componentParams (sector, archetype, ...) are applied LAST and OVERRIDE
//      both inbound and pre-existing values for the same key.
//
// === Attribution snapshot (ADR-009 — additive per-key) ===
//   On first mount AND on every subsequent navigation that has new attribution
//   params, `captureAttributionSnapshot()` MERGES into sessionStorage under
//   `ltg.attribution.v1`. The merge rule is per-key first-hop-wins:
//     - If a key is already in the snapshot, do NOT overwrite (preserves
//       attribution from the original landing visit).
//     - If a key is NEW (present in URL, missing from snapshot), ADD it.
//   This protects against partial snapshots from prior visits while still
//   honoring first-touch attribution semantics on a per-parameter basis.

export const UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "gclid",
    "fbclid",
    "li_fat_id",
    "msclkid",
];

const ATTRIBUTION_STORAGE_KEY = "ltg.attribution.v1";

// CHG-0113 · Marketing attribution capture on POST /api/contact.
// First-touch context (referrer / landing page / session id / first_touch_at)
// is captured ONCE per sessionStorage lifetime and preserved across SPA
// navigation so a user who lands with utm_source=linkedin, browses through
// /journals, and eventually submits on /contact still carries the original
// attribution. Kept under a distinct sessionStorage key from the UTM
// snapshot so ADR-009 first-hop-wins semantics for UTM keys remain
// independent of the first-touch metadata.
const FIRST_TOUCH_STORAGE_KEY = "ltg.first_touch.v1";

function getSearchParams() {
    if (typeof window === "undefined") return new URLSearchParams();
    try {
        return new URLSearchParams(window.location.search);
    } catch (_e) {
        return new URLSearchParams();
    }
}

// --- sessionStorage helpers ----------------------------------------------

function safeSessionStorage() {
    if (typeof window === "undefined") return null;
    try {
        return window.sessionStorage;
    } catch (_e) {
        return null;
    }
}

function readSnapshotRaw() {
    const store = safeSessionStorage();
    if (!store) return {};
    try {
        const raw = store.getItem(ATTRIBUTION_STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return {};
        const out = {};
        UTM_KEYS.forEach((k) => {
            if (typeof parsed[k] === "string" && parsed[k]) out[k] = parsed[k];
        });
        return out;
    } catch (_e) {
        return {};
    }
}

/**
 * Merge any UTM/click-id params present on the current URL into the
 * sessionStorage snapshot. Per-key first-hop-wins: existing keys are never
 * overwritten; missing keys are added. Returns the resulting snapshot.
 *
 * Safe to call multiple times. Idempotent when the URL has no new params.
 */
export function captureAttributionSnapshot() {
    const store = safeSessionStorage();
    if (!store) return readSnapshotRaw();
    try {
        const sp = getSearchParams();
        const urlSnap = {};
        UTM_KEYS.forEach((k) => {
            const v = sp.get(k);
            if (v) urlSnap[k] = v;
        });
        const existing = readSnapshotRaw();
        const merged = { ...existing };
        let mutated = false;
        for (const [k, v] of Object.entries(urlSnap)) {
            if (!merged[k]) {
                merged[k] = v;
                mutated = true;
            }
        }
        if (mutated) {
            try {
                store.setItem(
                    ATTRIBUTION_STORAGE_KEY,
                    JSON.stringify(merged)
                );
            } catch (_e) {
                // privacy-mode storage may throw on .setItem; swallow
            }
        }
        return merged;
    } catch (_e) {
        return readSnapshotRaw();
    }
}

export function getAttributionSnapshot() {
    return readSnapshotRaw();
}

// --- Inbound readers (snapshot-first, URL-fallback) -----------------------

export function getInboundUTMs() {
    const snap = readSnapshotRaw();
    const out = { ...snap };
    const sp = getSearchParams();
    UTM_KEYS.forEach((k) => {
        if (out[k]) return; // snapshot key wins per ADR-009
        const v = sp.get(k);
        if (v) out[k] = v;
    });
    return out;
}

export function getInboundSector() {
    const v = getSearchParams().get("sector");
    return v || null;
}

// --- CHG-0113 · First-touch metadata capture ------------------------------
//
// Captures document.referrer + window.location.pathname + a session UUID
// on the FIRST call per sessionStorage lifetime. Subsequent calls are
// no-ops (per-sessionStorage first-hop-wins — preserves the true landing
// context across all SPA navigation within a browser session). Emitted
// alongside the UTM snapshot into POST /api/contact so downstream
// consumers (ADR-031 producer/consumer to app.letstalkgrowth.com) get
// the campaign-source dimension.

function generateSessionId() {
    // Prefer crypto.randomUUID when available (all modern browsers as of 2024+).
    try {
        if (
            typeof crypto !== "undefined" &&
            typeof crypto.randomUUID === "function"
        ) {
            return crypto.randomUUID();
        }
    } catch (_e) {
        /* fall through */
    }
    // RFC-4122-ish fallback for environments without crypto.randomUUID
    // (should be extremely rare in the production browser matrix).
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Capture (once per sessionStorage lifetime) the first-touch context:
 *   - referrer      · document.referrer at first invocation
 *   - landing_page  · window.location.pathname at first invocation
 *   - session_id    · a UUID stable across the session
 *   - first_touch_at· ISO timestamp of first invocation
 *
 * Idempotent: if the key already exists in sessionStorage, returns the
 * stored value without mutation.
 */
export function captureFirstTouchContext() {
    const store = safeSessionStorage();
    if (!store) return null;
    try {
        const existing = store.getItem(FIRST_TOUCH_STORAGE_KEY);
        if (existing) {
            try {
                const parsed = JSON.parse(existing);
                if (parsed && typeof parsed === "object") return parsed;
            } catch (_e) {
                /* fall through to re-capture below */
            }
        }
        const snap = {
            referrer:
                (typeof document !== "undefined" && document.referrer) || "",
            landing_page:
                (typeof window !== "undefined" &&
                    window.location &&
                    window.location.pathname) ||
                "",
            session_id: generateSessionId(),
            first_touch_at: new Date().toISOString(),
        };
        try {
            store.setItem(FIRST_TOUCH_STORAGE_KEY, JSON.stringify(snap));
        } catch (_e) {
            /* privacy-mode may throw on setItem — return the snap regardless */
        }
        return snap;
    } catch (_e) {
        return null;
    }
}

export function getFirstTouchContext() {
    const store = safeSessionStorage();
    if (!store) return null;
    try {
        const raw = store.getItem(FIRST_TOUCH_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_e) {
        return null;
    }
}

/**
 * Build the combined attribution shape POST /api/contact expects under
 * `attribution.utm` — merges the sessionStorage UTM snapshot with the
 * first-touch metadata. Called from submitContact.js buildPayload().
 *
 * Missing / unset keys are simply absent from the returned object; the
 * shape is liberal — the backend accepts `attribution: Optional[dict]`
 * and persists it verbatim to Mongo (`contact_submissions.attribution.utm`),
 * so future CHG-0109 dashboard consumers can decide which keys they
 * consume.
 */
export function getAttributionForSubmit() {
    const utms = getInboundUTMs();
    const ft = getFirstTouchContext() || {};
    return { ...utms, ...ft };
}

// --- Decorator -------------------------------------------------------------

export function decorateAppLtgUrl(href, componentParams) {
    if (!href || typeof window === "undefined") return href;
    const params = componentParams || {};
    try {
        const url = new URL(href, window.location.origin);
        const utms = getInboundUTMs();
        Object.entries(utms).forEach(([k, v]) => {
            url.searchParams.set(k, v);
        });
        const inboundSector = getInboundSector();
        if (
            inboundSector &&
            (params.sector === undefined || params.sector === null) &&
            !url.searchParams.get("sector")
        ) {
            url.searchParams.set("sector", inboundSector);
        }
        Object.entries(params).forEach(([k, v]) => {
            if (v == null || v === "") return;
            url.searchParams.set(k, String(v));
        });
        return url.toString();
    } catch (_e) {
        return href;
    }
}
